import React, { useState, useEffect } from 'react';
import AreaActivityHeader from './AreaActivityHeader';
import AreaActivityMap from './AreaActivityMap';
import MusicDataPanel from './MusicDataPanel';
import EventsUsersPanel from './EventsUsersPanel';
import ActivityFeedFooter from './ActivityFeedFooter';
import SlideOutPanel from './SlideOutPanel';
import FilterBadge from './FilterBadge';
import RegionClock from './RegionClock';
import LastFmPulseGeo from './LastFmPulseGeo';

// Mobile region tabs configuration
const MOBILE_REGIONS = [
  { id: 'global', name: 'Global', emoji: '🌍', level: 'global' },
  { id: 'north_america', name: 'Americas', emoji: '🌎', level: 'continent' },
  { id: 'europe', name: 'Europe', emoji: '🇪🇺', level: 'continent' },
  { id: 'asia', name: 'Asia', emoji: '🌏', level: 'continent' },
  { id: 'oceania', name: 'Pacific', emoji: '🏝️', level: 'continent' },
  { id: 'africa', name: 'Africa', emoji: '🌍', level: 'continent' },
];

const AreaActivityModal = ({ user, BACKEND_URL }) => {
  // Mobile and orientation detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [showMobileMessage, setShowMobileMessage] = useState(false);
  
  // Check for mobile and orientation on mount and resize
  useEffect(() => {
    const checkMobileAndOrientation = () => {
      const mobile = window.innerWidth < 768 || (window.innerWidth < 1024 && window.innerHeight < 500);
      const landscape = window.innerWidth > window.innerHeight;
      setIsMobile(mobile);
      setIsLandscape(landscape);
    };
    
    checkMobileAndOrientation();
    window.addEventListener('resize', checkMobileAndOrientation);
    window.addEventListener('orientationchange', checkMobileAndOrientation);
    
    // Show mobile message if on mobile and hasn't been dismissed
    const isMobileDevice = window.innerWidth < 768 || (window.innerWidth < 1024 && window.innerHeight < 500);
    if (isMobileDevice) {
      const dismissed = localStorage.getItem('pulse_mobile_message_dismissed');
      if (!dismissed) {
        setShowMobileMessage(true);
      }
    }
    
    return () => {
      window.removeEventListener('resize', checkMobileAndOrientation);
      window.removeEventListener('orientationchange', checkMobileAndOrientation);
    };
  }, []);
  
  // Dismiss mobile message handler
  const dismissMobileMessage = () => {
    setShowMobileMessage(false);
    localStorage.setItem('pulse_mobile_message_dismissed', 'true');
  };

  // Region selection state
  const [selectedRegion, setSelectedRegion] = useState({
    level: 'global', // 'global', 'continent', 'country', 'state'
    id: 'global',
    name: 'Global',
    path: ['Global']
  });

  // Timeframe state
  const [timeFrame, setTimeFrame] = useState('Live');
  
  // New filter states
  const [viewMode, setViewMode] = useState('Current View');
  const [filterGenre, setFilterGenre] = useState('All Genres');
  const [sortBy, setSortBy] = useState('Activity');
  
  // Slide-out state
  const [slideOutOpen, setSlideOutOpen] = useState(null); // 'music', 'events', or null
  
  // Mobile tab selection state
  const [activeTab, setActiveTab] = useState('music'); // 'music' or 'events'

  // Handle region click (desktop map)
  const handleRegionClick = (region) => {
    setSelectedRegion(region);
  };
  
  // Handle mobile region tab selection
  const handleMobileRegionSelect = (region) => {
    setSelectedRegion({
      level: region.level,
      id: region.id,
      name: region.name,
      path: region.level === 'global' ? ['Global'] : ['Global', region.name]
    });
  };

  // ESC key navigation - go back one level
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        const path = selectedRegion.path;
        if (path.length > 1) {
          // Go back to previous level
          const previousLevel = path[path.length - 2];
          handleBreadcrumbClick(previousLevel);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedRegion]);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (level) => {
    if (level === 'Global') {
      setSelectedRegion({
        level: 'global',
        id: 'global',
        name: 'Global',
        path: ['Global']
      });
    } else {
      // Navigate back to the clicked level
      const currentPath = selectedRegion.path;
      const targetIndex = currentPath.indexOf(level);
      
      if (targetIndex !== -1) {
        const newPath = currentPath.slice(0, targetIndex + 1);
        
        // Determine the level based on path length
        let newLevel = 'global';
        let newId = 'global';
        
        if (newPath.length === 1) {
          newLevel = 'global';
          newId = 'global';
        } else if (newPath.length === 2) {
          newLevel = 'continent';
          // Convert continent name to ID
          newId = level.toLowerCase().replace(/\s+/g, '_');
        } else if (newPath.length === 3) {
          newLevel = 'country';
          // Convert country name to ID
          if (level === 'United States') newId = 'usa';
          else if (level === 'Canada') newId = 'canada';
          else if (level === 'Mexico') newId = 'mexico';
          else if (level === 'Greenland') newId = 'greenland';
          else newId = level.toLowerCase().replace(/\s+/g, '_');
        }
        
        setSelectedRegion({
          level: newLevel,
          id: newId,
          name: level,
          path: newPath
        });
      }
    }
  };

  // Handle view mode changes - trigger slide-outs
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'Music Focus') {
      setSlideOutOpen('music');
    } else if (mode === 'Events Focus') {
      setSlideOutOpen('events');
    } else {
      setSlideOutOpen(null);
    }
  };

  // Close slide-out
  const closeSlideOut = () => {
    setSlideOutOpen(null);
    setViewMode('Current View');
  };

  // Mobile Layout (Portrait & Landscape)
  if (isMobile) {
    return (
      <div className={`area-activity-modal pulse-mobile ${isLandscape ? 'pulse-landscape' : 'pulse-portrait'}`} style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        background: 'rgba(10, 14, 39, 0.98)',
        overflow: 'hidden'
      }}>
        {/* Mobile Message Overlay */}
        {showMobileMessage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isLandscape ? '10px' : '20px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.98), rgba(30, 20, 60, 0.98))',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '16px',
              padding: isLandscape ? '16px 24px' : '24px',
              maxWidth: isLandscape ? '400px' : '320px',
              textAlign: 'center',
              boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: isLandscape ? '28px' : '40px', marginBottom: isLandscape ? '8px' : '16px' }}>🎧</div>
              <h3 style={{
                color: '#00ffff',
                fontSize: isLandscape ? '16px' : '18px',
                fontWeight: '600',
                marginBottom: isLandscape ? '8px' : '12px'
              }}>
                The Pulse
              </h3>
              <p style={{
                color: '#94a3b8',
                fontSize: isLandscape ? '12px' : '14px',
                lineHeight: '1.5',
                marginBottom: isLandscape ? '12px' : '20px'
              }}>
                Works best on desktop or tablet for the full experience.
              </p>
              <button
                onClick={dismissMobileMessage}
                style={{
                  background: 'linear-gradient(135deg, #00ffff, #00cccc)',
                  color: '#0a0e27',
                  border: 'none',
                  borderRadius: '8px',
                  padding: isLandscape ? '8px 24px' : '12px 32px',
                  fontSize: isLandscape ? '12px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0, 255, 255, 0.3)'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        )}
        
        {/* Mobile Header - Compact for Landscape */}
        <div style={{
          padding: isLandscape ? '8px 12px' : '16px',
          borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
          background: 'rgba(10, 14, 39, 0.95)',
          display: 'flex',
          flexDirection: isLandscape ? 'row' : 'column',
          alignItems: isLandscape ? 'center' : 'stretch',
          gap: isLandscape ? '12px' : '0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: isLandscape ? '0' : '12px',
            flexShrink: 0
          }}>
            <h2 style={{
              color: '#00ffff',
              fontSize: isLandscape ? '14px' : '18px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>📡</span> {isLandscape ? 'PULSE' : 'THE PULSE'}
            </h2>
            {!isLandscape && <RegionClock selectedRegion={selectedRegion} compact />}
          </div>
          
          {/* Region Tabs - Inline for Landscape */}
          <div style={{
            display: 'flex',
            gap: isLandscape ? '6px' : '8px',
            overflowX: 'auto',
            paddingBottom: isLandscape ? '0' : '8px',
            flex: isLandscape ? 1 : 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} className="hide-scrollbar">
            {MOBILE_REGIONS.map((region) => (
              <button
                key={region.id}
                onClick={() => handleMobileRegionSelect(region)}
                style={{
                  flexShrink: 0,
                  padding: isLandscape ? '4px 10px' : '8px 14px',
                  borderRadius: '20px',
                  border: selectedRegion.id === region.id 
                    ? '1px solid #00ffff' 
                    : '1px solid rgba(100, 116, 139, 0.3)',
                  background: selectedRegion.id === region.id 
                    ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(0, 200, 200, 0.1))' 
                    : 'rgba(30, 41, 59, 0.5)',
                  color: selectedRegion.id === region.id ? '#00ffff' : '#94a3b8',
                  fontSize: isLandscape ? '11px' : '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>{region.emoji}</span>
                <span>{region.name}</span>
              </button>
            ))}
          </div>
          
          {isLandscape && <RegionClock selectedRegion={selectedRegion} compact />}
        </div>
        
        {/* LANDSCAPE: 2-Column Layout */}
        {isLandscape ? (
          <div style={{
            flex: 1,
            display: 'flex',
            gap: '8px',
            padding: '8px',
            overflow: 'hidden',
            minHeight: 0
          }}>
            {/* Left Column - Music */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(15, 20, 45, 0.6)',
              borderRadius: '10px',
              border: '1px solid rgba(0, 255, 255, 0.15)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid rgba(0, 255, 255, 0.15)',
                background: 'rgba(0, 255, 255, 0.05)',
                fontSize: '12px',
                fontWeight: '600',
                color: '#00ffff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🎵 Trending Music
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                <MusicDataPanel 
                  selectedRegion={selectedRegion} 
                  timeFrame={timeFrame}
                  filterGenre={filterGenre}
                  sortBy={sortBy}
                  isMobile={true}
                />
              </div>
            </div>
            
            {/* Right Column - Events */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(15, 20, 45, 0.6)',
              borderRadius: '10px',
              border: '1px solid rgba(255, 0, 255, 0.15)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid rgba(255, 0, 255, 0.15)',
                background: 'rgba(255, 0, 255, 0.05)',
                fontSize: '12px',
                fontWeight: '600',
                color: '#ff00ff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🎉 Events & Users
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                <EventsUsersPanel 
                  selectedRegion={selectedRegion} 
                  timeFrame={timeFrame}
                  filterGenre={filterGenre}
                  sortBy={sortBy}
                  isMobile={true}
                />
              </div>
            </div>
          </div>
        ) : (
          /* PORTRAIT: Tab-based Layout */
          <>
            {/* Mobile Content Tabs */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid rgba(0, 255, 255, 0.15)',
              background: 'rgba(15, 20, 45, 0.8)'
            }}>
              <button
                onClick={() => setActiveTab('music')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  background: 'transparent',
                  color: activeTab === 'music' ? '#00ffff' : '#64748b',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'music' ? '2px solid #00ffff' : '2px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                🎵 Trending Music
              </button>
              <button
                onClick={() => setActiveTab('events')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  background: 'transparent',
                  color: activeTab === 'events' ? '#ff00ff' : '#64748b',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'events' ? '2px solid #ff00ff' : '2px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                🎉 Events & Users
              </button>
            </div>
            
            {/* Mobile Content Area */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px'
            }}>
              {activeTab === 'music' ? (
                <MusicDataPanel 
                  selectedRegion={selectedRegion} 
                  timeFrame={timeFrame}
                  filterGenre={filterGenre}
                  sortBy={sortBy}
                  isMobile={true}
                />
              ) : (
                <EventsUsersPanel 
                  selectedRegion={selectedRegion} 
                  timeFrame={timeFrame}
                  filterGenre={filterGenre}
                  sortBy={sortBy}
                  isMobile={true}
                />
              )}
            </div>
            
            {/* Mobile Activity Feed - Portrait Only */}
            <div style={{
              padding: '10px 12px',
              background: 'rgba(10, 14, 39, 0.95)',
              borderTop: '1px solid rgba(0, 255, 255, 0.2)'
            }}>
              <ActivityFeedFooter selectedRegion={selectedRegion} compact />
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop Layout (original)
  return (
    <div className="area-activity-modal" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      background: 'rgba(10, 14, 39, 0.95)',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      {/* Header with breadcrumb, filters, and controls */}
      <AreaActivityHeader 
        selectedRegion={selectedRegion}
        onBreadcrumbClick={handleBreadcrumbClick}
        timeFrame={timeFrame}
        setTimeFrame={setTimeFrame}
        viewMode={viewMode}
        setViewMode={handleViewModeChange}
        filterGenre={filterGenre}
        setFilterGenre={setFilterGenre}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      
      {/* Filter Badges */}
      <div style={{
        padding: '10px 25px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        borderBottom: filterGenre !== 'All Genres' || sortBy !== 'Activity' ? '1px solid rgba(0, 255, 255, 0.2)' : 'none'
      }}>
        {filterGenre !== 'All Genres' && (
          <FilterBadge 
            icon="🎵"
            label={`Filtering: ${filterGenre}`}
            onClear={() => setFilterGenre('All Genres')}
            accentColor="#8A2BE2"
          />
        )}
        {sortBy !== 'Activity' && (
          <FilterBadge 
            icon={sortBy === 'Trending' ? '📈' : '🕐'}
            label={`Sort: ${sortBy}`}
            onClear={() => setSortBy('Activity')}
            accentColor="#FF00FF"
          />
        )}
      </div>
      
      {/* Main Content: 3-column layout (Left Panel | Map | Right Panel) */}
      <div className="pulse-main-content" style={{
        flex: 1,
        display: 'flex',
        gap: '12px',
        padding: '12px 15px 12px 55px',
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Left Panel - Music Data (22%) */}
        <div className="pulse-left-panel" style={{
          width: '22%',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          overflowY: 'auto',
          maxHeight: '100%',
          paddingRight: '5px'
        }}>
          <MusicDataPanel 
            selectedRegion={selectedRegion} 
            timeFrame={timeFrame}
            filterGenre={filterGenre}
            sortBy={sortBy}
          />
        </div>
        
        {/* Center - Map with Clock and Activity Feed (50%) */}
        <div className="pulse-center-panel" style={{
          width: '50%',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {/* Live Clock - Above Map */}
          <RegionClock selectedRegion={selectedRegion} />
          
          {/* Map Container with Animated Border */}
          <div style={{
            flex: 1,
            position: 'relative',
            padding: '5px',
            background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff)',
            backgroundSize: '200% 200%',
            animation: 'flashyBorder 4s linear infinite',
            borderRadius: '14px',
            boxShadow: '0 0 15px rgba(0, 255, 255, 0.5), 0 0 30px rgba(255, 0, 255, 0.3)',
            minHeight: 0
          }}>
            <style>{`
              @keyframes flashyBorder {
                0% { background-position: 0% 50%; }
                100% { background-position: 200% 50%; }
              }
            `}</style>
            
            {/* Inner Map Container */}
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(5, 7, 20, 0.95)',
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <AreaActivityMap 
                selectedRegion={selectedRegion}
                onRegionClick={handleRegionClick}
              />
            </div>
          </div>
          
          {/* Live Activity Feed - Below Map */}
          <div style={{
            padding: '10px 15px',
            background: 'rgba(10, 14, 39, 0.8)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <ActivityFeedFooter selectedRegion={selectedRegion} />
          </div>
        </div>
        
        {/* Right Panel - Events & Users (22%) */}
        <div className="pulse-right-panel" style={{
          width: '22%',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          overflowY: 'auto',
          maxHeight: '100%',
          paddingRight: '5px'
        }}>
          <EventsUsersPanel 
            selectedRegion={selectedRegion} 
            timeFrame={timeFrame}
            filterGenre={filterGenre}
            sortBy={sortBy}
          />
        </div>
      </div>
      
      {/* Music Focus Slide-Out from LEFT */}
      <SlideOutPanel
        isOpen={slideOutOpen === 'music'}
        onClose={closeSlideOut}
        side="left"
        title="MUSIC DEEP DIVE"
        icon="🎵"
        accentColor="#00FFFF"
      >
        <MusicDeepDive 
          selectedRegion={selectedRegion}
          timeFrame={timeFrame}
          filterGenre={filterGenre}
          sortBy={sortBy}
        />
      </SlideOutPanel>

      {/* Events Focus Slide-Out from RIGHT */}
      <SlideOutPanel
        isOpen={slideOutOpen === 'events'}
        onClose={closeSlideOut}
        side="right"
        title="EVENTS DEEP DIVE"
        icon="🎉"
        accentColor="#FF00FF"
      >
        <EventsDeepDive 
          selectedRegion={selectedRegion}
          timeFrame={timeFrame}
          filterGenre={filterGenre}
          sortBy={sortBy}
        />
      </SlideOutPanel>
    </div>
  );
};

// Music Deep Dive Content Component
const MusicDeepDive = ({ selectedRegion, timeFrame, filterGenre, sortBy }) => {
  const [musicData, setMusicData] = React.useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/area-activity/music?region_id=${selectedRegion.id}&timeframe=${timeFrame}`
        );
        const data = await response.json();
        setMusicData(data);
      } catch (error) {
        console.error('Error fetching music data:', error);
      }
    };
    fetchData();
  }, [selectedRegion.id, timeFrame, filterGenre, sortBy]);

  if (!musicData) {
    return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  // Apply genre filter
  let filteredTracks = musicData.trending_tracks || [];
  let filteredGenres = musicData.top_genres || [];
  
  if (filterGenre !== 'All Genres') {
    filteredTracks = filteredTracks.filter(t => t.genre === filterGenre);
    filteredGenres = filteredGenres.filter(g => g.genre === filterGenre);
  }

  // Apply sorting
  if (sortBy === 'Trending') {
    filteredGenres = [...filteredGenres].sort((a, b) => b.change - a.change);
    filteredTracks = [...filteredTracks].sort((a, b) => b.plays - a.plays);
  } else if (sortBy === 'Recent') {
    // For tracks, reverse order for "recent"
    filteredTracks = [...filteredTracks].reverse();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      {/* Last.fm Live Geo / Global Charts (real data) */}
      <LastFmPulseGeo selectedRegion={selectedRegion} filterGenre={filterGenre} />

      {/* Top Tracks Section */}
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#00FFFF',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>📊</span>
          <span>{timeFrame === 'Live' ? 'LIVE LISTENING' : 'TOP TRACKS'}</span>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
            ({filteredTracks.length} tracks)
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredTracks.slice(0, 15).map((track, idx) => (
            <div key={idx} style={{
              padding: '12px',
              background: 'rgba(0, 255, 255, 0.05)',
              borderRadius: '8px',
              borderLeft: '3px solid rgba(0, 255, 255, 0.5)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'translateX(5px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#00FFFF',
                marginBottom: '5px'
              }}>
                #{track.rank} {track.title}
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                {track.artist} • {track.genre}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                {track.plays.toLocaleString()} plays
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Genres Section */}
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#00FFFF',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>🎵</span>
          <span>TOP GENRES</span>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
            ({filteredGenres.length} genres)
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredGenres.map((genre, idx) => (
            <div key={idx} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px',
              background: 'rgba(0, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.2)';
            }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#00FFFF' }}>
                  {genre.genre}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                  {genre.plays.toLocaleString()} plays
                </div>
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: genre.change >= 0 ? '#22c55e' : '#ef4444'
              }}>
                {genre.change >= 0 ? '+' : ''}{genre.change}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Listening Stats */}
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#00FFFF',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>🎧</span>
          <span>LISTENING STATS</span>
        </div>
        <div style={{
          padding: '15px',
          background: 'rgba(0, 255, 255, 0.05)',
          borderRadius: '10px',
          border: '1px solid rgba(0, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Active Listeners</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#00FFFF' }}>
                {Math.floor(Math.random() * 2000 + 1000).toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Peak Hour</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#00FFFF' }}>
                {Math.floor(Math.random() * 4 + 20)}:00
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Avg Session</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#00FFFF' }}>
                {Math.floor(Math.random() * 60 + 30)} mins
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Events Deep Dive Content Component
const EventsDeepDive = ({ selectedRegion, timeFrame, filterGenre, sortBy }) => {
  const [eventsData, setEventsData] = React.useState(null);
  const [usersData, setUsersData] = React.useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, usersRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/area-activity/events?region_id=${selectedRegion.id}&region_name=${encodeURIComponent(selectedRegion.name || '')}&timeframe=${timeFrame}`),
          fetch(`${BACKEND_URL}/api/area-activity/users?region_id=${selectedRegion.id}&timeframe=${timeFrame}`)
        ]);
        const events = await eventsRes.json();
        const users = await usersRes.json();
        setEventsData(events);
        setUsersData(users);
      } catch (error) {
        console.error('Error fetching events data:', error);
      }
    };
    fetchData();
  }, [selectedRegion.id, timeFrame, filterGenre, sortBy]);

  if (!eventsData || !usersData) {
    return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  // Apply genre filter
  let filteredEvents = eventsData.upcoming || [];
  let filteredLiveEvents = eventsData.live_events || [];
  let filteredUsers = usersData.active_now || [];
  let filteredHotspots = usersData.hotspots || [];

  if (filterGenre !== 'All Genres') {
    const needle = String(filterGenre).toLowerCase();
    const matchGenre = (g) => typeof g === 'string' && g.toLowerCase().includes(needle);
    filteredEvents = filteredEvents.filter(e => matchGenre(e.genre));
    filteredLiveEvents = filteredLiveEvents.filter(e => matchGenre(e.genre));
    filteredUsers = filteredUsers.filter(u => matchGenre(u.genre));
    filteredHotspots = filteredHotspots.filter(h => matchGenre(h.genre));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      {/* Live Events Section (only for Live timeframe) */}
      {timeFrame === 'Live' && (
        <div>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#FF00FF',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🔴</span>
            <span>LIVE NOW</span>
          </div>
          {filteredLiveEvents.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredLiveEvents.map((event, idx) => (
                <div key={idx} style={{
                  padding: '14px',
                  background: 'rgba(255, 0, 255, 0.05)',
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 0, 255, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateX(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 255, 0.05)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#FF00FF',
                    marginBottom: '6px'
                  }}>
                    🔴 {event.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {event.artist} • {event.venue}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    📍 {event.city || ''}{event.attendees ? ` • ${event.attendees} attending` : ''}
                    {event.url && (
                      <a href={event.url} target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', marginLeft: '6px', textDecoration: 'underline' }}>tickets ↗</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '30px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
              fontStyle: 'italic',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '10px',
              border: '1px dashed rgba(255, 0, 255, 0.2)'
            }}>
              No events going on here at this time
            </div>
          )}
        </div>
      )}

      {/* Upcoming Events */}
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#FF00FF',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>🎉</span>
          <span>UPCOMING EVENTS</span>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
            ({filteredEvents.length} events)
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredEvents.slice(0, 20).map((event, idx) => (
            <div key={idx} style={{
              padding: '14px',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '10px',
              border: '1px solid rgba(255, 0, 255, 0.2)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 255, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 255, 0.03)';
              e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.2)';
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#FF00FF',
                marginBottom: '6px'
              }}>
                {event.name}
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
                {event.artist}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>
                📍 {event.city || ''}
                {event.date ? ` • ${event.date}` : (event.start_iso ? ` • ${new Date(event.start_iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : '')}
                {typeof event.attendees === 'number' ? ` • ${event.attendees.toLocaleString()} attending` : ''}
                {event.url && (
                  <a href={event.url} target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', marginLeft: '6px', textDecoration: 'underline' }}>tickets ↗</a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hotspots (for historical timeframes) */}
      {timeFrame !== 'Live' && (
        <div>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#FF00FF',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🏆</span>
            <span>HOTSPOTS</span>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
              ({filteredHotspots.length} locations)
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filteredHotspots.slice(0, 15).map((spot, idx) => (
              <div key={idx} style={{
                padding: '12px',
                background: 'rgba(255, 0, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 255, 0.2)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 0, 255, 0.05)';
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#FF00FF',
                  marginBottom: '4px'
                }}>
                  📍 {spot.location}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {spot.activity_count} activities • {spot.genre} • Peak: {spot.peak_time}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Users */}
      <div>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#FF00FF',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>👥</span>
          <span>ACTIVE USERS</span>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
            ({filteredUsers.length} users)
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredUsers.slice(0, 25).map((user, idx) => (
            <div key={idx} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              background: 'rgba(255, 0, 255, 0.03)',
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 255, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 255, 0.03)';
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#FF00FF' }}>
                  {user.username}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  {user.city} • {user.status}
                </div>
              </div>
              <div style={{
                fontSize: '10px',
                padding: '4px 8px',
                background: 'rgba(255, 0, 255, 0.15)',
                borderRadius: '10px',
                color: '#FF00FF',
                fontWeight: '600'
              }}>
                {user.genre}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AreaActivityModal;
